import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import CategoryFilter from '@/components/CategoryFilter';
import SolutionGrid from '@/components/SolutionGrid';
import AddSolutionDialog from '@/components/AddSolutionDialog';
import { Input } from '@/components/ui/input';
import { Search, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSolutionFiltering } from '@/hooks/useSolutionFiltering';
import DynamicMetaTags from '@/components/DynamicMetaTags';

const Explore = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use the shared filtering hook with enhanced tag-based filtering
  const {
    categories,
    filteredSolutions,
    selectedCategory,
    searchQuery,
    isLoading,
    isFiltering,
    isError,
    filterError,
    setSelectedCategory,
    setSearchQuery,
    refetch,
    clearFilters,
    getCategoryName
  } = useSolutionFiltering('explore-solutions-categories');
  
  // Handle direct navigation from /categories/:categoryName
  useEffect(() => {
    if (location.pathname.startsWith('/categories/') && categories.length > 0) {
      const categoryName = location.pathname.split('/').pop();
      const category = categories.find(c => c.name.toLowerCase() === categoryName?.toLowerCase());
      if (category) {
        setSelectedCategory(category.id);
        navigate('/explore', { replace: true });
      }
    }
  }, [location.pathname, categories, navigate, setSelectedCategory]);

  const handleAddSolution = () => {
    setIsAddDialogOpen(true);
  };

  // Show content even if there are errors
  const showContent = !isError || filteredSolutions.length > 0 || !isLoading;

  return (
    <Layout>
      <DynamicMetaTags
        title="Explore AI Solutions - AIZen.tips"
        description="Browse and explore our comprehensive collection of AI solutions across various categories. Find AI tools for your specific needs."
        canonical="https://aizen.tips/explore"
      />
      
      <div className="container mx-auto px-4 py-8 sm:py-12 md:py-16 lg:py-24">
        <div className="space-y-6 md:space-y-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 md:gap-6">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight mb-2 sm:mb-3 text-left">Explore AI Solutions</h1>
              <p className="text-muted-foreground max-w-2xl text-left text-sm sm:text-base">
                Browse our comprehensive collection of AI solutions across various categories.
                Filter by category or search to find exactly what you're looking for.
              </p>
            </div>
            
            <Button 
              className="gap-2 w-full md:w-auto md:flex-shrink-0 min-h-[44px]"
              size="sm"
              onClick={handleAddSolution}
            >
              <PlusCircle className="h-4 w-4" />
              Add Solution
            </Button>
          </div>
          
          {showContent && (
            <>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                  <div className="relative flex-1 min-w-0">
                    <Input
                      type="text"
                      placeholder="Search solutions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-full"
                    />
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  </div>
                  
                  <CategoryFilter 
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                    isLoading={isLoading}
                    isFiltering={isFiltering}
                    className="w-full sm:w-auto sm:flex-shrink-0"
                  />
                </div>
              </div>
              
              {/* Solutions grid */}
              <SolutionGrid 
                solutions={filteredSolutions}
                isLoading={isLoading || isFiltering}
                selectedCategory={selectedCategory}
                categoryName={getCategoryName(selectedCategory)}
              />
            </>
          )}
          
          {/* Empty state for no results */}
          {!isLoading && !isFiltering && filteredSolutions.length === 0 && (selectedCategory || searchQuery.trim()) && (
            <div className="text-center py-12">
              <h3 className="text-lg sm:text-xl font-medium mb-2">No solutions found</h3>
              <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                Try different search terms, clear your filters, or check back later for more solutions.
              </p>
              <Button 
                onClick={clearFilters} 
                variant="outline"
                className="min-h-[44px] px-6"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>

      <AddSolutionDialog 
        open={isAddDialogOpen} 
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          // Refetch data when dialog closes after potential success
          if (!open) {
            refetch();
          }
        }}
      />
    </Layout>
  );
};

export default Explore;
